/**
 * Date utilities using date-fns for formatting and duration calculations
 *
 * Expected translation keys:
 * - date.duration.lessThanMinute: "Less than a minute"
 * - date.duration.unknown: "Unknown duration"
 * - date.relative.justNow: "Just now"
 * - date.relative.ago: "{duration} ago"
 * - date.relative.in: "in {duration}"
 */

import { format, intervalToDuration, formatDuration, parseISO, isValid, parse, startOfWeek, getDay } from 'date-fns';
import { enUS, fr } from 'date-fns/locale';
import { fromZonedTime, toZonedTime, formatInTimeZone } from 'date-fns-tz';

// Locale mapping
const localeMap = {
    'en': enUS,
    'fr': fr
};

// Timezone constants
const PARIS_TIMEZONE = 'Europe/Paris';

/**
 * Force UTC interpretation function for calendar dates
 * This function treats local dates as if they were UTC times, then converts to Paris timezone
 * @param {Date|string} dateInput - The date input to convert
 * @returns {Date|null} - The converted date in Paris timezone or null
 */
export const forceUtcToParisTime = (dateInput) => {
  if (!dateInput) return null;

  try {
    let utcDate;

    if (dateInput instanceof Date) {
      // Force interpret as UTC by using the local time values as UTC
      const year = dateInput.getFullYear();
      const month = dateInput.getMonth();
      const day = dateInput.getDate();
      const hours = dateInput.getHours();
      const minutes = dateInput.getMinutes();
      const seconds = dateInput.getSeconds();

      // Create UTC date with these values
      utcDate = new Date(Date.UTC(year, month, day, hours, minutes, seconds));
    } else if (typeof dateInput === 'string') {
      // Parse string as UTC
      if (!dateInput.endsWith('Z') && !dateInput.includes('+') && !dateInput.includes('-', 10)) {
        utcDate = new Date(dateInput + (dateInput.includes('T') ? 'Z' : 'T00:00:00Z'));
      } else {
        utcDate = new Date(dateInput);
      }
    } else {
      utcDate = new Date(dateInput);
    }

    // Convert to Paris timezone
    return toZonedTime(utcDate, PARIS_TIMEZONE);
  } catch (error) {
    console.warn('Error converting date to Paris timezone:', error);
    return new Date(dateInput);
  }
};

/**
 * Convert Paris time to UTC for backend communication
 * @param {Date} parisDate - The date in Paris timezone
 * @returns {Date|null} - The UTC date or null
 */
export const convertParisTimeToUtc = (parisDate) => {
  if (!parisDate) return null;

  try {
    // Convert Paris time to UTC
    const utcDate = fromZonedTime(parisDate, PARIS_TIMEZONE);
    return utcDate;
  } catch (error) {
    console.warn('Error converting Paris time to UTC:', error);
    return parisDate; // Fallback to original date
  }
};

/**
 * Format date in specific timezone
 * @param {Date} date - The date to format
 * @param {string} timezone - The timezone (defaults to Paris)
 * @param {string} formatStr - The format string
 * @param {object} options - Additional options including locale
 * @returns {string} - Formatted date string
 */
export const formatDateInTimezone = (date, timezone = PARIS_TIMEZONE, formatStr = 'PPp', options = {}) => {
  if (!date) return '';

  try {
    return formatInTimeZone(date, timezone, formatStr, options);
  } catch (error) {
    console.warn('Error formatting date in timezone:', error);
    return format(date, formatStr, options);
  }
};

/**
 * Get calendar formats for Paris timezone
 * @param {object} locale - The date-fns locale object
 * @returns {object} - Calendar format configuration
 */
export const getParisTimezoneCalendarFormats = (locale) => {
  return {
    monthHeaderFormat: (date) => formatInTimeZone(date, PARIS_TIMEZONE, 'MMMM yyyy', { locale }),
    dayHeaderFormat: (date) => formatInTimeZone(date, PARIS_TIMEZONE, 'EEEE, MMMM d', { locale }),
    dayRangeHeaderFormat: ({ start, end }) =>
      `${formatInTimeZone(start, PARIS_TIMEZONE, 'MMMM d', { locale })} - ${formatInTimeZone(end, PARIS_TIMEZONE, 'MMMM d, yyyy', { locale })}`,
    agendaHeaderFormat: ({ start, end }) =>
      `${formatInTimeZone(start, PARIS_TIMEZONE, 'MMMM d', { locale })} - ${formatInTimeZone(end, PARIS_TIMEZONE, 'MMMM d, yyyy', { locale })}`,
    agendaDateFormat: (date) => formatInTimeZone(date, PARIS_TIMEZONE, 'EEE MMM dd', { locale }),
    agendaTimeFormat: (date) => formatInTimeZone(date, PARIS_TIMEZONE, 'HH:mm', { locale }),
    agendaTimeRangeFormat: ({ start, end }) =>
      `${formatInTimeZone(start, PARIS_TIMEZONE, 'HH:mm', { locale })} – ${formatInTimeZone(end, PARIS_TIMEZONE, 'HH:mm', { locale })}`,
  };
};

/**
 * Create a custom react-big-calendar localizer that uses Paris timezone
 * @param {function} dateFnsLocalizer - The dateFnsLocalizer function from react-big-calendar
 * @param {object} locales - The locales object mapping language codes to date-fns locales
 * @returns {object} - The configured localizer
 */
export const createParisTimezoneLocalizer = (dateFnsLocalizer, locales) => {
  return dateFnsLocalizer({
    format: (date, formatStr, culture) => {
      // Use formatInTimeZone to ensure consistent Paris timezone formatting
      const locale = locales[culture] || locales['en'];
      return formatInTimeZone(date, PARIS_TIMEZONE, formatStr, { locale });
    },
    parse: (dateStr, formatStr, culture) => {
      const locale = locales[culture] || locales['en'];
      const parsedDate = parse(dateStr, formatStr, new Date(), { locale });
      // Convert parsed date to Paris timezone
      return toZonedTime(parsedDate, PARIS_TIMEZONE);
    },
    startOfWeek: (date, culture) => {
      const locale = locales[culture] || locales['en'];
      const startWeek = startOfWeek(date, { locale });
      return toZonedTime(startWeek, PARIS_TIMEZONE);
    },
    getDay: (date) => {
      // Ensure we get the day in Paris timezone
      const parisDate = toZonedTime(date, PARIS_TIMEZONE);
      return getDay(parisDate);
    },
    locales,
  });
};

/**
 * Get the appropriate date-fns locale based on language code
 * @param {string} languageCode - The language code (e.g., 'en', 'fr')
 * @returns {object} - The date-fns locale object
 */
const getLocale = (languageCode) => {
    return localeMap[languageCode] || enUS;
};

/**
 * Formats a date string into a human-readable format using date-fns with Paris timezone
 * @param {string} dateString - The date string to format
 * @param {boolean} includeTime - Whether to include time in the format
 * @param {string} languageCode - The language code for locale
 * @param {function} t - Translation function (optional, for custom format patterns)
 * @param {boolean} forceUtcToParis - Whether to force UTC interpretation and convert to Paris time
 * @param {boolean} includeDayName - Whether to include the day name in the format
 * @returns {string} - Formatted date string or '-' if invalid
 */
export const formatDate = (dateString, includeTime = true, languageCode = 'en', t = null, forceUtcToParis = true, includeDayName = false) => {
    if (!dateString) return '-';

    try {
        let date;

        if (forceUtcToParis) {
            // Use the force UTC to Paris conversion for consistent timezone handling
            date = forceUtcToParisTime(dateString);
        } else {
            date = parseISO(dateString);
        }

        if (!date || !isValid(date)) return '-';

        const locale = getLocale(languageCode);

        // Use custom format from translations if available, otherwise use default
        let formatPattern;
        if (t) {
            if (includeTime) {
                formatPattern = includeDayName
                    ? t('date.formats.longWithTimeAndDay', 'EEEE, PP, p')
                    : t('date.formats.longWithTime', 'PP, p');
            } else {
                formatPattern = includeDayName
                    ? t('date.formats.longWithDay', 'EEEE, PP')
                    : t('date.formats.long', 'PP');
            }
        } else {
            if (includeTime) {
                formatPattern = includeDayName ? 'EEEE, PP, p' : 'PP, p';
            } else {
                formatPattern = includeDayName ? 'EEEE, PP' : 'PP';
            }
        }

        // Format in Paris timezone if we're using timezone conversion
        if (forceUtcToParis) {
            return formatInTimeZone(date, PARIS_TIMEZONE, formatPattern, { locale });
        } else {
            return format(date, formatPattern, { locale });
        }
    } catch (error) {
        console.warn('Error formatting date:', error);
        return '-';
    }
};

/**
 * Simple date formatting for lists (without time) using Paris timezone
 * @param {string} dateString - The date string to format
 * @param {string} languageCode - The language code for locale
 * @param {function} t - Translation function (optional, for custom format patterns)
 * @param {boolean} forceUtcToParis - Whether to force UTC interpretation and convert to Paris time
 * @returns {string} - Formatted date string or '-' if invalid
 */
export const formatDateSimple = (dateString, languageCode = 'en', t = null, forceUtcToParis = true) => {
    return formatDate(dateString, false, languageCode, t, forceUtcToParis);
};

/**
 * Short date formatting for compact displays using Paris timezone
 * @param {string} dateString - The date string to format
 * @param {boolean} includeTime - Whether to include time in the format
 * @param {string} languageCode - The language code for locale
 * @param {function} t - Translation function (optional, for custom format patterns)
 * @param {boolean} forceUtcToParis - Whether to force UTC interpretation and convert to Paris time
 * @returns {string} - Formatted date string or '-' if invalid
 */
export const formatDateShort = (dateString, includeTime = false, languageCode = 'en', t = null, forceUtcToParis = true) => {
    if (!dateString) return '-';

    try {
        let date;

        if (forceUtcToParis) {
            // Use the force UTC to Paris conversion for consistent timezone handling
            date = forceUtcToParisTime(dateString);
        } else {
            date = parseISO(dateString);
        }

        if (!date || !isValid(date)) return '-';

        const locale = getLocale(languageCode);

        // Use custom short format from translations if available
        let formatPattern;
        if (t) {
            if (includeTime) {
                formatPattern = t('date.formats.shortWithTime', 'P, p');
            } else {
                formatPattern = t('date.formats.short', 'P');
            }
        } else {
            formatPattern = includeTime ? 'P, p' : 'P';
        }

        // Format in Paris timezone if we're using timezone conversion
        if (forceUtcToParis) {
            return formatInTimeZone(date, PARIS_TIMEZONE, formatPattern, { locale });
        } else {
            return format(date, formatPattern, { locale });
        }
    } catch (error) {
        console.warn('Error formatting short date:', error);
        return '-';
    }
};

/**
 * Calculates and formats the duration between two dates using Paris timezone
 * @param {string} startDate - The start date
 * @param {string} endDate - The end date (optional, defaults to current date)
 * @param {function} t - Translation function (optional)
 * @param {string} languageCode - The language code for locale (optional)
 * @param {boolean} forceUtcToParis - Whether to force UTC interpretation and convert to Paris time
 * @returns {string} - Formatted duration text
 */
export const getDurationText = (startDate, endDate, t, languageCode = 'en', forceUtcToParis = true) => {
    if (!startDate) return '';

    try {
        let start, end;

        if (forceUtcToParis) {
            // Convert dates to Paris timezone for accurate duration calculation
            start = forceUtcToParisTime(startDate);
            end = endDate ? forceUtcToParisTime(endDate) : new Date();
        } else {
            start = parseISO(startDate);
            end = endDate ? parseISO(endDate) : new Date();
        }

        if (!start || !end || !isValid(start) || !isValid(end)) {
            console.warn('Invalid dates in getDurationText:', { startDate, endDate });
            return '';
        }

        // Calculate the interval duration
        const duration = intervalToDuration({ start, end });
        const locale = getLocale(languageCode);

        // Format duration with appropriate units
        const formatOptions = {
            format: ['years', 'months', 'weeks', 'days', 'hours', 'minutes'],
            locale
        };

        // If we have years or months, only show those
        if (duration.years > 0 || duration.months > 0) {
            formatOptions.format = ['years', 'months'];
        }
        // If we have weeks or days, show those
        else if (duration.weeks > 0 || duration.days > 0) {
            formatOptions.format = ['weeks', 'days'];
        }
        // If less than a day, show hours and minutes
        else {
            formatOptions.format = ['hours', 'minutes'];
        }

        const formattedDuration = formatDuration(duration, formatOptions);

        // Return formatted duration or fallback to simple format
        return formattedDuration || (t ? t('date.duration.lessThanMinute', 'Less than a minute') : 'Less than a minute');

    } catch (error) {
        console.warn('Error calculating duration:', error);
        // Fallback to a simple format if date-fns fails
        if (t) {
            return t('date.duration.unknown', 'Unknown duration');
        }
        return '';
    }
};/**
 * Calculates and formats the duration for reservations using Paris timezone
 * @param {string} reservedFrom - The reservation start date
 * @param {string} reservedUntil - The reservation end date
 * @param {function} t - Translation function (optional)
 * @param {string} languageCode - The language code for locale (optional)
 * @param {boolean} forceUtcToParis - Whether to force UTC interpretation and convert to Paris time
 * @returns {string} - Formatted reservation duration text
 */
export const getReservationDurationText = (reservedFrom, reservedUntil, t, languageCode = 'en', forceUtcToParis = true) => {
    if (!reservedFrom || !reservedUntil) return '';
    return getDurationText(reservedFrom, reservedUntil, t, languageCode, forceUtcToParis);
};

/**
 * Creates a simplified duration formatter that uses Paris timezone
 * @param {string} languageCode - The language code to use for formatting
 * @param {boolean} forceUtcToParis - Whether to force UTC interpretation and convert to Paris time
 * @returns {function} - A function that formats durations with the preset language
 */
export const createDurationFormatter = (languageCode = 'en', forceUtcToParis = true) => {
    return (startDate, endDate, t) => {
        return getDurationText(startDate, endDate, t, languageCode, forceUtcToParis);
    };
};

/**
 * Creates a simplified reservation duration formatter that uses Paris timezone
 * @param {string} languageCode - The language code to use for formatting
 * @param {boolean} forceUtcToParis - Whether to force UTC interpretation and convert to Paris time
 * @returns {function} - A function that formats reservation durations with the preset language
 */
export const createReservationDurationFormatter = (languageCode = 'en', forceUtcToParis = true) => {
    return (reservedFrom, reservedUntil, t) => {
        return getReservationDurationText(reservedFrom, reservedUntil, t, languageCode, forceUtcToParis);
    };
};

/**
 * Formats a relative time (e.g., "2 days ago", "in 3 hours") using Paris timezone
 * @param {string} dateString - The date string to format
 * @param {function} t - Translation function (optional)
 * @param {string} languageCode - The language code for locale (optional)
 * @param {boolean} forceUtcToParis - Whether to force UTC interpretation and convert to Paris time
 * @returns {string} - Formatted relative time text
 */
export const formatRelativeTime = (dateString, t, languageCode = 'en', forceUtcToParis = true) => {
    if (!dateString) return '';

    try {
        let date;

        if (forceUtcToParis) {
            date = forceUtcToParisTime(dateString);
        } else {
            date = parseISO(dateString);
        }

        const now = new Date();

        if (!date || !isValid(date)) return '';

        const duration = intervalToDuration({
            start: date < now ? date : now,
            end: date < now ? now : date
        });

        const isPast = date < now;
        const locale = getLocale(languageCode);

        // Format the duration
        let formattedDuration = '';
        if (duration.years > 0) {
            formattedDuration = formatDuration(duration, { format: ['years'], locale });
        } else if (duration.months > 0) {
            formattedDuration = formatDuration(duration, { format: ['months'], locale });
        } else if (duration.days > 0) {
            formattedDuration = formatDuration(duration, { format: ['days'], locale });
        } else if (duration.hours > 0) {
            formattedDuration = formatDuration(duration, { format: ['hours'], locale });
        } else if (duration.minutes > 0) {
            formattedDuration = formatDuration(duration, { format: ['minutes'], locale });
        } else {
            return t ? t('date.relative.justNow', 'Just now') : 'Just now';
        }

        // Add relative context
        if (isPast) {
            return t ? t('date.relative.ago', `${formattedDuration} ago`, { duration: formattedDuration }) : `${formattedDuration} ago`;
        } else {
            return t ? t('date.relative.in', `in ${formattedDuration}`, { duration: formattedDuration }) : `in ${formattedDuration}`;
        }

    } catch (error) {
        console.warn('Error formatting relative time:', error);
        return '';
    }
};

/**
 * Checks if a date is overdue (past the expected date) using Paris timezone
 * @param {string} expectedDate - The expected date string
 * @param {string} actualDate - The actual date string (optional, defaults to current date)
 * @param {boolean} forceUtcToParis - Whether to force UTC interpretation and convert to Paris time
 * @returns {boolean} - True if overdue, false otherwise
 */
export const isOverdue = (expectedDate, actualDate = null, forceUtcToParis = true) => {
    if (!expectedDate) return false;

    try {
        let expected, actual;

        if (forceUtcToParis) {
            expected = forceUtcToParisTime(expectedDate);
            actual = actualDate ? forceUtcToParisTime(actualDate) : new Date();
        } else {
            expected = parseISO(expectedDate);
            actual = actualDate ? parseISO(actualDate) : new Date();
        }

        if (!expected || !actual || !isValid(expected) || !isValid(actual)) return false;

        return actual > expected;
    } catch (error) {
        console.warn('Error checking overdue status:', error);
        return false;
    }
};


/**
 * Creates a date in the future based on the given duration
 * @param {object} duration - Duration object with hours, days, weeks, months, years
 * @param {Date} baseDate - Base date to add duration to (defaults to now)
 * @returns {Date} - The calculated future date
 */
export const addDuration = (duration, baseDate = new Date()) => {
    const result = new Date(baseDate);

    if (duration.years) {
        result.setFullYear(result.getFullYear() + duration.years);
    }
    if (duration.months) {
        result.setMonth(result.getMonth() + duration.months);
    }
    if (duration.weeks) {
        result.setDate(result.getDate() + (duration.weeks * 7));
    }
    if (duration.days) {
        result.setDate(result.getDate() + duration.days);
    }
    if (duration.hours) {
        result.setHours(result.getHours() + duration.hours);
    }
    if (duration.minutes) {
        result.setMinutes(result.getMinutes() + duration.minutes);
    }

    return result;
};

/**
 * Generates a date for a quick preset by parsing the key directly
 * @param {string} presetKey - The preset key (e.g., '1h', '2d', '7d')
 * @param {Date} baseDate - Base date to add duration to (defaults to now)
 * @returns {Date|null} - The calculated date or null if key format is invalid
 */
export const getQuickDate = (presetKey, baseDate = new Date()) => {
    if (!presetKey) return null;

    // Parse the key format: number + unit (e.g., '1h', '2d', '7d')
    const match = presetKey.match(/^(\d+)([hdwmy])$/);
    if (!match) return null;

    const [, value, unit] = match;
    const numValue = parseInt(value, 10);

    const duration = {};
    switch (unit) {
        case 'h':
            duration.hours = numValue;
            break;
        case 'd':
            duration.days = numValue;
            break;
        case 'w':
            duration.weeks = numValue;
            break;
        case 'm':
            duration.months = numValue;
            break;
        case 'y':
            duration.years = numValue;
            break;
        default:
            return null;
    }

    return addDuration(duration, baseDate);
};

/**
 * Formats a date for datetime-local input using Paris timezone
 * @param {Date|string} date - The date to format
 * @param {boolean} forceUtcToParis - Whether to force UTC interpretation and convert to Paris time
 * @returns {string} - Formatted date string for datetime-local input
 */
export const formatForDateTimeInput = (date, forceUtcToParis = true) => {
    if (!date) return '';

    try {
        let processedDate;

        if (forceUtcToParis && typeof date === 'string') {
            processedDate = forceUtcToParisTime(date);
        } else if (date instanceof Date) {
            processedDate = date;
        } else {
            processedDate = new Date(date);
        }

        if (!processedDate || !isValid(processedDate)) return '';

        // Format as YYYY-MM-DDTHH:MM for datetime-local input
        const year = processedDate.getFullYear();
        const month = String(processedDate.getMonth() + 1).padStart(2, '0');
        const day = String(processedDate.getDate()).padStart(2, '0');
        const hours = String(processedDate.getHours()).padStart(2, '0');
        const minutes = String(processedDate.getMinutes()).padStart(2, '0');

        return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch (error) {
        console.warn('Error formatting date for input:', error);
        return '';
    }
};

// Convenience functions for legacy compatibility (without Paris timezone conversion)
export const formatDateLocal = (dateString, includeTime = true, languageCode = 'en', t = null) =>
    formatDate(dateString, includeTime, languageCode, t, false);

export const formatDateSimpleLocal = (dateString, languageCode = 'en', t = null) =>
    formatDateSimple(dateString, languageCode, t, false);

export const formatDateShortLocal = (dateString, includeTime = false, languageCode = 'en', t = null) =>
    formatDateShort(dateString, includeTime, languageCode, t, false);

export const getDurationTextLocal = (startDate, endDate, t, languageCode = 'en') =>
    getDurationText(startDate, endDate, t, languageCode, false);

export const getReservationDurationTextLocal = (reservedFrom, reservedUntil, t, languageCode = 'en') =>
    getReservationDurationText(reservedFrom, reservedUntil, t, languageCode, false);

export const formatRelativeTimeLocal = (dateString, t, languageCode = 'en') =>
    formatRelativeTime(dateString, t, languageCode, false);

export const isOverdueLocal = (expectedDate, actualDate = null) =>
    isOverdue(expectedDate, actualDate, false);

export const formatForDateTimeInputLocal = (date) =>
    formatForDateTimeInput(date, false);


/**
 * French locale for react-multi-date-picker
 */
export const gregorian_fr = {
  name: "gregorian_fr",
  months: [
    ["Janvier", "Jan"],
    ["Février", "Fév"],
    ["Mars", "Mar"],
    ["Avril", "Avr"],
    ["Mai", "Mai"],
    ["Juin", "Jun"],
    ["Juillet", "Jul"],
    ["Août", "Aoû"],
    ["Septembre", "Sep"],
    ["Octobre", "Oct"],
    ["Novembre", "Nov"],
    ["Décembre", "Déc"],
  ],
  weekDays: [
    ["Samedi", "Sam"],
    ["Dimanche", "Dim"],
    ["Lundi", "Lun"],
    ["Mardi", "Mar"],
    ["Mercredi", "Mer"],
    ["Jeudi", "Jeu"],
    ["Vendredi", "Ven"],
  ],
  digits: ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"],
  meridiems: [
    ["AM", "am"],
    ["PM", "pm"],
  ],
};

/**
 * French calendar for react-multi-date-picker
 */
export const calendar_fr = {
  name: "gregorian_fr",
  startYear: 1,
  yearLength: 365,
  epoch: 1721424,
  century: 20,
  weekStartDayIndex: 2,
  getMonthLengths(isLeap) {
    return [31, isLeap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  },
  isLeap(year) {
    return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
  },
  getLeaps(currentYear) {
    if (currentYear === 0) return;

    let year = currentYear > 0 ? 1 : -1;

    let leaps = [],
      condition = () =>
        currentYear > 0 ? year <= currentYear : currentYear <= year,
      increase = () => (currentYear > 0 ? year++ : year--);

    while (condition()) {
      if (this.isLeap(year)) leaps.push(year);

      increase();
    }

    return leaps;
  },
  getDayOfYear({ year, month, day }) {
    let monthLengths = this.getMonthLengths(this.isLeap(year));

    for (let i = 0; i < month.index; i++) {
      day += monthLengths[i];
    }

    return day;
  },
  getAllDays(date) {
    const { year } = date;

    return (
      this.yearLength * (year - 1) +
      this.leapsLength(year) +
      this.getDayOfYear(date)
    );
  },
  leapsLength(year) {
    return (
      (((year - 1) / 4) | 0) +
      (-((year - 1) / 100) | 0) +
      (((year - 1) / 400) | 0)
    );
  },
  guessYear(days, currentYear) {
    let year = ~~(days / 365.24);

    return year + (currentYear > 0 ? 1 : -1);
  },
};
